import { alpha } from "@mui/material/styles";

import { buttonBaseClasses } from "@mui/material/ButtonBase";
import { dividerClasses } from "@mui/material/Divider";
import { menuItemClasses } from "@mui/material/MenuItem";
import { svgIconClasses } from "@mui/material/SvgIcon";
import { tabClasses } from "@mui/material/Tab";
import { gray, brand, indigo, white } from "../themePrimitives";
import { typographyClasses } from "@mui/material/Typography";

export const navigationCustomisations = {
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: (theme.vars || theme).shape.borderRadius,
        padding: "6px 6px",
        // Default hover
        "&:hover": {
          backgroundColor: alpha(brand[500], 0.09),
          color: indigo[200],
          [`& .${svgIconClasses.root}`]: { color: indigo[200] },
          [`& .${typographyClasses.root}`]: { color: indigo[200] },
        },
        // Selected state
        [`&.${menuItemClasses.selected}`]: {
          backgroundColor: indigo[200],
          [`& .${svgIconClasses.root}`]: { color: white[500] },
          "& .MuiListItemText-primary, & .MuiTypography-root": {
            color: white[500],
          },
          "&:hover": {
            backgroundColor: indigo[300],
            color: white[500],
            [`& .${svgIconClasses.root}`]: { color: white[500] },
            "& .MuiListItemText-primary, & .MuiTypography-root": {
              color: white[500],
            },
          },
        },
      }),
    },
    variants: [
      {
        props: { color: "error" },
        style: ({ theme }) => ({
          color: theme.palette.error.main,
          [`& .${svgIconClasses.root}, & .${typographyClasses.root}`]: {
            color: theme.palette.error.main,
          },
          "&:hover": {
            backgroundColor: alpha(theme.palette.error.main, 0.08),
            color: theme.palette.error.main,
            [`& .${svgIconClasses.root}, & .${typographyClasses.root}`]: {
              color: theme.palette.error.main,
            },
          },
        }),
      },
    ],
  },

  MuiMenu: {
    styleOverrides: {
      list: {
        gap: "0px",
        [`&.${dividerClasses.root}`]: {
          margin: "0 -8px",
        },
      },
      paper: ({ theme }) => ({
        marginTop: "4px",
        borderRadius: (theme.vars || theme).shape.borderRadius,
        border: `1px solid ${(theme.vars || theme).palette.divider}`,
        backgroundImage: "none",
        background: white[500],
        boxShadow: "0 14px 36px rgba(32, 30, 90, 0.12)",
        [`& .${buttonBaseClasses.root}`]: {
          "&.Mui-selected": {
            backgroundColor: alpha(theme.palette.action.selected, 0.3),
          },
        },
        ...theme.applyStyles("dark", {
          background: gray[900],
          boxShadow:
            "hsla(220, 30%, 5%, 0.7) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.8) 0px 8px 16px -5px",
        }),
      }),
    },
  },
  MuiLink: {
    defaultProps: {
      underline: "none",
    },
    styleOverrides: {
      root: ({ theme }) => ({
        color: (theme.vars || theme).palette.text.primary,
        fontWeight: 500,
        position: "relative",
        textDecoration: "none",
        width: "fit-content",
        "&::before": {
          content: '""',
          position: "absolute",
          width: "100%",
          height: "1px",
          bottom: 0,
          left: 0,
          backgroundColor: (theme.vars || theme).palette.text.secondary,
          opacity: 0.3,
          transition: "width 0.3s ease, opacity 0.3s ease",
        },
        "&:hover::before": {
          width: 0,
        },
        "&:focus-visible": {
          outline: `3px solid ${alpha(brand[500], 0.5)}`,
          outlineOffset: "4px",
          borderRadius: "2px",
        },
      }),
    },
  },
  MuiPaginationItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        "&.Mui-selected": {
          color: white[500],
          backgroundColor: indigo[200],
        },
        ...theme.applyStyles("dark", {
          "&.Mui-selected": {
            color: theme.palette.common.black,
            backgroundColor: (theme.vars || theme).palette.grey[50],
          },
        }),
      }),
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: { minHeight: "fit-content" },
      indicator: ({ theme }) => ({
        backgroundColor: (theme.vars || theme).palette.primary.main,
      }),
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: "6px 8px",
        marginBottom: "8px",
        textTransform: "none",
        minWidth: "fit-content",
        minHeight: "fit-content",
        color: (theme.vars || theme).palette.text.secondary,
        borderRadius: (theme.vars || theme).shape.borderRadius,
        border: "1px solid",
        borderColor: "transparent",
        ":hover": {
          color: (theme.vars || theme).palette.primary.main,
        },
        [`&.${tabClasses.selected}`]: {
          color: (theme.vars || theme).palette.secondary.main,
          fontWeight: 600,
        },
      }),
    },
  },
  MuiStepConnector: {
    styleOverrides: {
      line: ({ theme }) => ({
        borderTop: "1px solid",
        borderColor: (theme.vars || theme).palette.divider,
        flex: 1,
        borderRadius: "99px",
      }),
    },
  },
  MuiStepIcon: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: gray[200],
        border: `none`,
        borderRadius: "50%",
        "&.Mui-active": {
          color: (theme.vars || theme).palette.primary.main,
        },
        "&.Mui-completed": {
          color: (theme.vars || theme).palette.primary.main,
        },
      }),
    },
  },
  MuiStepLabel: {
    styleOverrides: {
      label: () => ({
        "&.Mui-completed": {
          opacity: 0.6,
        },
      }),
    },
  },
};
